import {SourcePMEIIdsByTemplatePMDVIdREST} from 'procedures/network/ProcedureRunDAO';
import {ExperimentProcedure} from 'studies/typed/process-development/ExperimentalInputs';
import {UnitOperationFields} from 'studies/typed/process-development/unit-op-linking-flowchart/UnitOperationFlowchartNode';
import {PMEIEdge} from 'studies/typed/process-development/unit-op-linking-flowchart/UnitOpLinkingFlowchartEditor';
import {Condition} from 'studies/typed/process-development/useProcessDevelopmentStudyState';

/**
 * Helper function that generates a unique PMEI ID based on the method name, condition name, and replicate number.
 * Method names are unique within a procedure, and conditions are unique within a method, so this ID is guaranteed to be unique.
 */
function getDestinationPMEIId(methodName: string, conditionName: string, replicateNumber: number): string {
  return `${methodName}-${conditionName}-${replicateNumber}`;
}

/**
 * Helper function that iterates over all method, condition, and replicate combinations.
 * Calls the provided callback function for each combination.
 */
function forEachDestinationPMEI(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null,
  callback: (methodName: string, conditionName: string, replicateNumber: number) => void
): void {
  if (procedure != null) {
    procedure.methods.forEach((method) => {
      if (conditions != null) {
        conditions.forEach((condition) => {
          for (let i = 1; i <= condition.numReplicates; i++) {
            callback(method.name, condition.name, i);
          }
        });
      }
    });
  }
}

export function getDestinationPMEIIds(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Set<string> {
  const destinationPMEIIds = new Set<string>();
  forEachDestinationPMEI(procedure, conditions, (methodName, conditionName, replicateNumber) => {
    const pmeiId = getDestinationPMEIId(methodName, conditionName, replicateNumber);
    destinationPMEIIds.add(pmeiId);
  });
  return destinationPMEIIds;
}

export function getDestinationPMEIs(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null,
  experimentName: string | null
): Array<UnitOperationFields> {
  const orderedDestinationPMEIs: Array<UnitOperationFields> = [];
  forEachDestinationPMEI(procedure, conditions, (methodName, conditionName, replicateNumber) => {
    orderedDestinationPMEIs.push({
      pmeiId: getDestinationPMEIId(methodName, conditionName, replicateNumber),
      procedureRunName: experimentName,
      methodName,
      conditionName,
      replicateNumber,
    });
  });
  return orderedDestinationPMEIs;
}

function getDestinationPMEIToTemplatePMDVIdMapping(
  destinationPMEIs: Array<UnitOperationFields> | null,
  procedure: ExperimentProcedure | null
): Record<string, string> {
  const destinationPMEIsToTemplatePMDVs: Record<string, string> = {};
  if (procedure != null && destinationPMEIs != null) {
    procedure.methods.forEach((method) => {
      destinationPMEIs.forEach((destinationPMEI) => {
        if (method.name === destinationPMEI.methodName) {
          const templatePMDVId = method.id;
          if (templatePMDVId != null) {
            destinationPMEIsToTemplatePMDVs[destinationPMEI.pmeiId] = templatePMDVId;
          }
        }
      });
    });
  }
  return destinationPMEIsToTemplatePMDVs;
}

function getDestinationPMEIToConditionReplicateMapping(
  destinationPMEIs: Array<UnitOperationFields> | null,
  conditions: Array<Condition> | null
): Record<string, {conditionIndex: number; replicateIndex: number}> {
  const destinationPMEIIdsToConditionReplicateMapping: Record<
    string,
    {conditionIndex: number; replicateIndex: number}
  > = {};
  if (destinationPMEIs != null && conditions != null) {
    destinationPMEIs.forEach((destinationPMEI) => {
      const conditionIndex = conditions.findIndex(
        (condition) => condition.name === destinationPMEI.conditionName
      );
      const replicateIndex = destinationPMEI.replicateNumber - 1;
      destinationPMEIIdsToConditionReplicateMapping[destinationPMEI.pmeiId] = {
        conditionIndex,
        replicateIndex,
      };
    });
  }
  return destinationPMEIIdsToConditionReplicateMapping;
}

/* Create a mapping of source PMEIs to template PMDVs for each condition and replicate.
 */
export function createSourcePMEIsToTemplatePMDVsMapping(
  newLinks: Array<PMEIEdge> | null,
  destinationPMEIs: Array<UnitOperationFields> | null,
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null
): Array<Array<SourcePMEIIdsByTemplatePMDVIdREST>> {
  if (newLinks == null || destinationPMEIs == null || procedure == null || conditions == null) {
    return [];
  }
  const sourcePMEIIdsByTemplatePMDVId: Array<Array<Record<string, Array<string>>>> = [];
  for (let i = 0; i < conditions.length; i++) {
    const numReplicates = conditions[i].numReplicates;
    const replicateArray: Array<Record<string, Array<string>>> = [];
    for (let j = 0; j < numReplicates; j++) {
      replicateArray[j] = {};
    }
    sourcePMEIIdsByTemplatePMDVId[i] = replicateArray;
  }

  const destinationPMEIsToTemplatePMDVIds: Record<string, string> = getDestinationPMEIToTemplatePMDVIdMapping(
    destinationPMEIs,
    procedure
  );
  const destinationPMEIIdsToConditionReplicateIndices = getDestinationPMEIToConditionReplicateMapping(
    destinationPMEIs,
    conditions
  );
  if (procedure != null) {
    newLinks?.forEach((edge) => {
      const sourcePMEIId = edge.sourcePMEIId;
      const destinationPMEIId = edge.destinationPMEIId;
      const templatePMDVId = destinationPMEIsToTemplatePMDVIds[destinationPMEIId];
      const {conditionIndex, replicateIndex} =
        destinationPMEIIdsToConditionReplicateIndices[destinationPMEIId];
      if (templatePMDVId != null) {
        if (!sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex][templatePMDVId]) {
          sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex][templatePMDVId] = [];
        }
        sourcePMEIIdsByTemplatePMDVId[conditionIndex][replicateIndex][templatePMDVId].push(sourcePMEIId);
      }
    });
  }
  return sourcePMEIIdsByTemplatePMDVId;
}
